import { SunOutlined } from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { Gym, HolidayWithGyms, LocationClosure } from '@hefe/models';
import { GymsSlice, LocationClosuresApi, ModalsSlice } from '@hefe/store';
import { formatDate } from '@hefe/utils';
import { Button, Modal, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { useAppDispatch } from '../../../../store/store';
import CustomTable from '../../../core/components/table/custom-table.component';
import SyncHolidaysComponent from '../../components/sync-holidays/sync-holidays.component';
import AddEditGymClosureForm from '../../forms/add-edit-gym-closure/add-edit-gym-closure.form';

export default function GymClosuresPage() {
  const dispatch = useAppDispatch();

  const { isFetching, data } =
    LocationClosuresApi.useFetchLocationClosuresQuery();

  const [saveMultipleClosures, { isLoading: isSavingMultiple }] =
    LocationClosuresApi.useCreateMultipleLocationClosuresMutation();

  const [deleteLocationClosure, { isLoading: isDeleting }] =
    LocationClosuresApi.useDeleteLocationClosureMutation();

  const [syncHolidaysModalOpen, setSyncHolidaysModalOpen] = useState(false);
  const [holidaySyncData, setHolidaySyncData] = useState<HolidayWithGyms[]>([]);

  useEffect(() => {
    fetchGyms();
  }, []);

  const fetchGyms = () => {
    dispatch(
      GymsSlice.fetchGyms({
        cacheDuration: 5 * 60 * 1000,
      })
    );
  };

  const deleteClosure = (id: number) => {
    const closure = data?.find((c) => c.id === id);
    if (!closure) return;

    deleteLocationClosure(closure);
  };

  const onLocationClosureClick = (locationClosure: LocationClosure) => {
    dispatch(
      ModalsSlice.modalsActions.openModal({
        title: t('gyms.closures.edit'),
        component: <AddEditGymClosureForm closure={locationClosure} />,
        formId: `add-edit-gym-closure-form-${locationClosure.id}`,
      })
    );
  };

  const saveHolidays = () => {
    if (!holidaySyncData?.length) return;

    saveMultipleClosures(
      holidaySyncData.map((d) => ({
        closed_from: d.from,
        closed_until: d.until,
        reason: d.name,
        gym_ids: d.gym_ids,
      }))
    )
      .unwrap()
      .then(() => setSyncHolidaysModalOpen(false))
      .catch(() => {});
  };

  return (
    <>
      <CustomTable
        name="location-closures"
        rowKey="id"
        dataSource={data ?? []}
        loading={isFetching}
        columns={[
          {
            key: 'reason',
            title: t('gyms.closures.reason'),
            dataIndex: 'reason',
            default: true,
          },
          {
            key: 'closed_from',
            title: t('from'),
            dataIndex: 'closed_from',
            default: true,
            render: (value) => formatDate(value),
          },
          {
            key: 'closed_until',
            title: t('till'),
            dataIndex: 'closed_until',
            default: true,
            render: (value) => formatDate(value),
          },
          {
            key: 'gyms',
            title: t('gyms.plural'),
            dataIndex: 'gyms',
            default: true,
            render: (value: Gym[]) => value?.map((gym) => gym.name).join(', '),
          },
        ]}
        addForm={{
          component: <AddEditGymClosureForm />,
          title: t('gyms.closures.add'),
          formId: 'add-edit-gym-closure-form-new',
        }}
        deleteAction={{
          action: (data, resetKeys) => deleteClosure(data[0]),
          loading: isDeleting,
          multiDelete: false,
          text: t('gyms.closures.delete'),
        }}
        onRowClick={(locationClosure) =>
          onLocationClosureClick(locationClosure)
        }
        toolbarItems={[
          <Tooltip
            title={t('gyms.closures.vacations.sync.title')}
            showArrow={false}
          >
            <Button
              icon={<SunOutlined />}
              onClick={() => setSyncHolidaysModalOpen(true)}
            />
          </Tooltip>,
        ]}
      />
      <Modal
        width={'80%'}
        open={syncHolidaysModalOpen}
        onCancel={() => setSyncHolidaysModalOpen(false)}
        okText={t('save')}
        onOk={() => {
          saveHolidays();
        }}
        okButtonProps={{ loading: isSavingMultiple }}
      >
        <SyncHolidaysComponent setHolidaySyncData={setHolidaySyncData} />
      </Modal>
    </>
  );
}
